import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { SHOWCASE } from "@/data/showcase";

const INTERVAL_MS = 5000;

function useVisibleCount() {
  const [n, setN] = useState(3);
  useEffect(() => {
    const mqLg = window.matchMedia("(min-width: 1024px)");
    const mqMd = window.matchMedia("(min-width: 640px)");
    const calc = () => setN(mqLg.matches ? 3 : mqMd.matches ? 2 : 1);
    calc();
    mqLg.addEventListener("change", calc);
    mqMd.addEventListener("change", calc);
    return () => {
      mqLg.removeEventListener("change", calc);
      mqMd.removeEventListener("change", calc);
    };
  }, []);
  return n;
}

export default function ProjectShowcase() {
  const visible = useVisibleCount();
  const maxIndex = SHOWCASE.length - visible;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), INTERVAL_MS);
    return () => clearInterval(timer.current);
  }, [paused, maxIndex]);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const go = (dir) => setIndex((i) => (i + dir < 0 ? maxIndex : i + dir > maxIndex ? 0 : i + dir));

  return (
    <section className="border-y border-slate-100 bg-brand-paper py-16 md:py-24" id="our-work" data-testid="showcase-section">
      <div className="container-x">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Eyebrow className="mb-4 block">Real jobs, real results</Eyebrow>
            <FadeUp>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                Drag to see the <span className="text-brand-red">before and after.</span>
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                A few recent jobs from across Sydney - photographed by our own installers, not stock images.
                Slide the handle on any photo to compare the job before we started with the finished result.
              </p>
            </FadeUp>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous projects"
              className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-slate-700 transition-colors hover:border-brand-red hover:text-brand-red"
              data-testid="showcase-prev-btn"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next projects"
              className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-slate-700 transition-colors hover:border-brand-red hover:text-brand-red"
              data-testid="showcase-next-btn"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          className="mt-10 overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
          data-testid="showcase-track"
        >
          <div
            className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(-${(index * 100) / visible}%)` }}
          >
            {SHOWCASE.map((item, i) => (
              <article
                key={item.key}
                className="w-full shrink-0 px-3 sm:w-1/2 lg:w-1/3"
                data-testid={`showcase-card-${i}`}
              >
                <BeforeAfterSlider before={item.before} after={item.after} beforeLabel={item.beforeLabel} />
                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-red">{item.service}</p>
                    <h3 className="mt-1 font-display text-sm font-extrabold leading-snug text-slate-900 sm:text-base">
                      {item.title}
                    </h3>
                  </div>
                  <Link
                    to={item.href}
                    className="group mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition-colors hover:border-brand-red hover:bg-brand-red hover:text-white"
                    aria-label={`See more ${item.service.toLowerCase()} projects`}
                    data-testid={`showcase-card-${i}-link`}
                  >
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2" data-testid="showcase-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-brand-red" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
              data-testid={`showcase-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
